<template>
  <div class="bed-timeline">
    <ul class="timeline-legend list-unstyled d-flex flex-wrap mb-3">
      <li v-for="status in statuses" :key="status" class="me-3 mb-1 d-flex align-items-center">
        <span class="legend-swatch me-1" :class="'slot-' + status"></span>
        <span class="small">{{ statusText(status) }}</span>
      </li>
    </ul>

    <div class="timeline-scroll">
      <div class="timeline-grid">
        <div v-for="row in rows" :key="row.key" class="timeline-row">
          <div class="timeline-label small text-muted">{{ row.label }}</div>
          <div class="timeline-bar" role="img" :aria-label="row.label">
            <div
              v-for="(slot, index) in row.slots"
              :key="index"
              class="timeline-slot"
              :class="'slot-' + slot.status"
              :title="slot.title"
            ></div>
          </div>
        </div>

        <div class="timeline-row timeline-axis-row">
          <div class="timeline-label"></div>
          <div class="timeline-axis">
            <span
              v-for="(tick, index) in ticks"
              :key="index"
              class="timeline-tick small text-muted"
              :class="{ 'tick-minor': index % 2 === 1 }"
              :style="{ left: tick.percent + '%' }"
            >{{ tick.text }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  TIMELINE_STATUSES,
  TIMELINE_STATUS_NONE,
  TIMELINE_SLOT_MINUTES,
  MINUTES_PER_HOUR,
  formatMinutesOfDay,
  formatWindowLabel,
  windowStartMinutes
} from '@/components/monthlyReport/format'

const STATUS_LABEL_KEYS = {
  leave: '__monthlyReport.timelineLeave',
  lying: '__monthlyReport.timelineLying',
  sleep: '__monthlyReport.timelineSleep',
  none: '__monthlyReport.timelineNone'
}

const AXIS_HOURS = 24
// 後端合約的觀察窗從中午 12:00 起算
const DEFAULT_START_MINUTES = 12 * MINUTES_PER_HOUR

export default {
  name: 'BedTimeline30Days',
  props: {
    // 每筆 { date, start, end, slots: ['leave'|'lying'|'sleep'|'none', ...] }
    windows: {
      type: Array,
      required: true
    }
  },
  computed: {
    statuses () {
      return TIMELINE_STATUSES
    },
    startMinutes () {
      const first = this.windows[0]
      const minutes = first ? windowStartMinutes(first.start) : null
      return minutes === null ? DEFAULT_START_MINUTES : minutes
    },
    statusTexts () {
      return TIMELINE_STATUSES.reduce((texts, status) => {
        texts[status] = this.$t(STATUS_LABEL_KEYS[status])
        return texts
      }, {})
    },
    rows () {
      return this.windows.map(item => {
        const startMinutes = windowStartMinutes(item.start)
        const baseMinutes = startMinutes === null ? this.startMinutes : startMinutes
        return {
          key: item.date,
          label: formatWindowLabel(item.start, item.end),
          slots: (item.slots || []).map((rawStatus, index) => {
            const status = STATUS_LABEL_KEYS[rawStatus] ? rawStatus : TIMELINE_STATUS_NONE
            const slotStart = baseMinutes + index * TIMELINE_SLOT_MINUTES
            const range = `${formatMinutesOfDay(slotStart)}-${formatMinutesOfDay(slotStart + TIMELINE_SLOT_MINUTES)}`
            return { status, title: `${range} ${this.statusTexts[status]}` }
          })
        }
      })
    },
    ticks () {
      const startHour = Math.floor(this.startMinutes / MINUTES_PER_HOUR)
      return Array.from({ length: AXIS_HOURS + 1 }, (unused, index) => ({
        text: formatMinutesOfDay((startHour + index) * MINUTES_PER_HOUR).slice(0, 2),
        percent: (index / AXIS_HOURS) * 100
      }))
    }
  },
  methods: {
    statusText (status) {
      return this.statusTexts[status]
    }
  }
}
</script>

<style scoped>
.slot-leave { background-color: #c4c4c4; }
.slot-lying { background-color: #09a2b7; }
.slot-sleep { background-color: #ffbc3a; }
.slot-none { background-color: #f1f1f1; }

.legend-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 1px solid #ced4da;
  border-radius: 2px;
}

.timeline-scroll {
  overflow-x: auto;
}
.timeline-grid {
  min-width: 640px;
}
.timeline-row {
  display: flex;
  align-items: center;
  margin-bottom: 3px;
}
.timeline-label {
  flex: 0 0 160px;
  padding-right: 8px;
  white-space: nowrap;
}
.timeline-bar {
  flex: 1 1 auto;
  display: flex;
  height: 16px;
  border: 1px solid #dee2e6;
  border-radius: 2px;
  overflow: hidden;
}
.timeline-slot {
  flex: 1 1 0;
}
.timeline-slot + .timeline-slot {
  border-left: 1px solid rgba(255, 255, 255, 0.6);
}

.timeline-axis-row {
  margin-top: 4px;
  margin-bottom: 0;
}
.timeline-axis {
  position: relative;
  flex: 1 1 auto;
  height: 18px;
}
.timeline-tick {
  position: absolute;
  top: 0;
  transform: translateX(-50%);
  line-height: 18px;
}

@media (max-width: 991.98px) {
  .timeline-tick.tick-minor {
    display: none;
  }
}
</style>
